import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Switch, Redirect } from "react-router-dom";
import { BrowserRouter as Router } from "react-router-dom";
import { PrivateRoute } from "components/PrivateRoute";
import SubscriptionRoute from "components/SubscriptionRoute";
import {
	authService,
	globalSocketService,
	SESSION_ID,
	notificationsSocketService,
} from "services";
import CustomHelmet from "components/Layout/CustomHelmet";
import Dashboard from "pages/Dashboard";
import GettingStarted from "pages/GettingStarted";
import Times from "pages/Times";
import TimesWithout from "pages/TimesWithout";
import TimesBalance from "pages/TimesBalance";
import Register from "pages/Register";
import Login from "pages/Login";
import LoginAttempts from "pages/LoginAttempts";
import RememberPassword from "pages/RememberPassword";
import NewPassword from "pages/NewPassword";
import Invitation from "pages/Invitation";
import DuplicatedEmail from "pages/DuplicatedEmail";

import Clockin from "pages/Clockin";

import NotFound from "pages/NotFound";
import Profile from "pages/Profile";
import CustomFields from "pages/CustomFields";
import Employee from "pages/Employee";
import Employees from "pages/Employees";
import EmployeeFunctions from "pages/EmployeeFunctions";
import EmployeeUbications from "pages/EmployeeUbications";
import EmployeeImports from "pages/EmployeeImports";
import EmployeeShiftPatternsImports from "pages/EmployeeShiftPatternsImports";
import EmployeeFunctionsImports from "pages/EmployeeFunctionsImports";
import EmployeeUbicationsImports from "pages/EmployeeUbicationsImports";
import Forecasting from "pages/Forecasting";
import FunctionsImports from "pages/FunctionsImports";
import UbicationsImports from "pages/UbicationsImports";
import TimeRegistrations from "pages/TimeRegistrations";
import TimeRegistrationsImports from "pages/TimeRegistrationsImports";
import Group from "pages/Group";
import Groups from "pages/Groups";
import StaffingLevels from "pages/StaffingLevels";
import Absences from "pages/Absences";
import AbsencesPending from "pages/AbsencesPending";
import AbsencesManaged from "pages/AbsencesManaged";
import Notifications from "pages/Notifications";
import AbsenceImports from "pages/AbsenceImports";
import Documents from "pages/Documents";
import ExpensesResponsive from "pages/ExpensesResponsive";
import MobileExpenseFormResponsive from "pages/MobileExpenseFormResponsive";
import ExpenseGroupings from "pages/ExpenseGroupings";
import ExpenseTypes from "pages/ExpenseTypes";
import PaymentTypes from "pages/PaymentTypes";
import PendingExpensesResponsive from "pages/PendingExpensesResponsive";
import ApprovedExpensesResponsive from "pages/ApprovedExpensesResponsive";
import ManagedExpenses from "pages/ExpensesManaged";

import Shifts from "pages/Shifts";
import ShiftPatterns from "pages/ShiftPatterns";
import ShiftsImports from "pages/ShiftsImports";
import ShiftPatternsImports from "pages/ShiftPatternsImports";
import Preferences from "pages/Preferences";
import PendingPreferences from "pages/PreferencesPending";
import ManagedPreferences from "pages/PreferencesManaged";
import ShiftExchanges from "pages/ShiftExchanges";
import ShiftExchangesPending from "pages/ShiftExchangesPending";
import ShiftExchangesManaged from "pages/ShiftExchangesManaged";
import StaffingLevelsImports from "pages/StaffingLevelsImports";
import Calendars from "pages/Calendars";
import CalendarImports from "pages/CalendarImports";
import SystemCalendars from "pages/SystemCalendars";
import SystemCalendarImports from "pages/SystemCalendarImports";
import CalendarResponsive from "pages/CalendarResponsive";
import DayTypes from "pages/DayTypes";
import TimeTypes from "pages/TimeTypes";
import Accounts from "pages/Accounts";
import Contracts from "pages/Contracts";
import ContractsImports from "pages/ContractsImports";
import Plans from "pages/Plans";
import Plan from "pages/Plan";
import PlanResponsive from "pages/PlanResponsive";
import PlanMagic from "pages/PlanMagic";
import PlanViews from "pages/PlanViews";
import PlanImports from "pages/PlanImports";
import Providers from "pages/Providers";

import Rules from "pages/Rules";
import Transactions from "pages/Transactions";
import TransactionsImports from "pages/TransactionImports";

import Subscriptions from "pages/Subscriptions";
import MyCompany from "pages/MyCompany";
import MySubscription from "pages/MySubscription";
import EndTrial from "pages/EndTrial";
import Restricted from "pages/Restricted";
import WidgetTemplates from "pages/WidgetTemplates";
import * as userActions from "actions/userActions";

import StoreFolders from "pages/StoreFolders";
import Settings from "pages/Settings";
import Integrations from "pages/Integrations";
import Loading from "components/Loading";
import ZendeskWidget from "components/ZendeskWidget";
import AppUndoBindings from "components/Undo/AppUndoBindings";
import "styles/App.scss";
import AttendanceSupervisorResponsive from "pages/AttendanceSupervisorResponsive";
import AttendanceByEmployee from "pages/AttendanceByEmployee";
import {
	buildPosthogIdentifySignature,
	identifyHaddockUser,
	trackHaddockAdminLogin,
} from "utils/posthogTracking";
import { isHaddockHostname } from "utils/haddockDomain";

function getSkinFromDomain(domain) {
	if (isHaddockHostname(domain)) {
		return "haddock";
	}
	return "default";
}
// Detecta dominio y skin ANTES DEL RENDER
const domain = window.location.hostname;
const skinFolder = getSkinFromDomain(domain);
document.documentElement.classList.add(`custom-${skinFolder}`);
document.documentElement.classList.add("custom-domain");

function App() {
	const dispatch = useDispatch(); // Hook para despachar acciones
	const [loading, setLoading] = useState(true); // Estado para manejar la carga
	const user = useSelector((state) => state.authReducer?.user); // Obtener usuario del store
	const posthogIdentifySignatureRef = useRef(null);
	const posthogLoginTrackedUserIdRef = useRef(null);

	const domainSpecificRoutes = isHaddockHostname(domain)
		? [
					<Route
						key="restricted-my-subscription"
						exact
						path="/my-subscription"
						component={Restricted}
					/>,
					<Route
						key="restricted-contract-plans"
						exact
						path="/contract-plans"
						render={(props) => <Restricted {...props} mode="end-trial" />}
					/>,
				]
			: [
					<PrivateRoute
						key="private-my-subscription"
						exact
						path="/my-subscription"
						roles={["ADMIN"]}
						component={MySubscription}
					/>,
					<PrivateRoute
						key="private-contract-plans"
						exact
						path="/contract-plans"
						roles={["ADMIN"]}
						component={MySubscription}
					/>,
				];

	useEffect(() => {
		async function performSSO() {
			try {
				// Si venimos de un cambio de empresa, refrescar siempre el usuario
				const isSwitchingCompany = sessionStorage.getItem("switching_company");
				if (isSwitchingCompany) {
					try {
						await dispatch(userActions.update());
					} finally {
						sessionStorage.removeItem("switching_company");
						setLoading(false);
					}
					return;
				}

				// SSO Haddock: dominio HR o cookie de handoff (p. ej. redirect desde Haddock Business)
				if (
					authService.isHaddockDomain ||
					(typeof document !== "undefined" &&
						document.cookie.includes("__plain_session="))
				) {
					authService.isFromHaddock = true;
				}

				if (authService.isFromHaddock) {
					await dispatch(userActions.sso());
				}
			} catch (error) {
				console.log("Error en SSO:", error);
			} finally {
				setLoading(false); // Independientemente del resultado, terminamos la carga
			}
		}

		performSSO(); // Llama a performSSO si viene de Haddock
	}, [dispatch]);

	// Ref para rastrear el empleado actualmente suscrito (evita suscripciones dobles o stale)
	const subscribedEmployeeIdRef = useRef(null);
	const initializingRef = useRef(null); // Para rastrear si hay una inicialización en curso para un ID
	const [wsAuthEpoch, setWsAuthEpoch] = useState(0);

	// Otra pestaña puede cambiar el token de venue; hay que re-suscribir con el JWT nuevo.
	useEffect(() => {
		const onStorage = (event) => {
			if (event.key === "token") {
				authService.syncTokenFromStorage();
				setWsAuthEpoch((n) => n + 1);
			}
		};
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, []);

	// Inicializar WebSocket cuando la sesión esté lista.
	// El topic /topic/alert-notifications/{id} se autoriza contra el employeeId
	// del JWT ("Logged on"), no contra user.id de localStorage. En multivenue
	// son IDs distintos del mismo empleado; suscribir el ID persistido de la
	// otra venue provoca Unauthorized. La planificación no padece esto porque
	// usa group_id de la vista actual, no un employeeId persistido.
	useEffect(() => {
		let isMounted = true;

		async function initializeWebSocket() {
			if (loading || !isMounted) return;

			const rawEmployeeId =
				authService.getJwtEmployeeId() ||
				(user || authService.currentUser)?.id;
			const employeeId =
				rawEmployeeId != null ? Number(rawEmployeeId) : null;
			if (!employeeId || !Number.isFinite(employeeId)) return;

			// Si el servicio está gestionando una reconexión automática (ej: backend cerró la sesión),
			// no interferir — el servicio restaurará las suscripciones por sí solo
			if (globalSocketService.isAutoReconnecting()) {
				console.log(
					`📡 [App][SESSION:${SESSION_ID}] Servicio en reconexión automática, omitiendo intervención de App.js`
				);
				return;
			}

			// Si ya hay una inicialización en curso para este mismo ID, no hacer nada
			if (initializingRef.current === employeeId) {
				return;
			}
			const previousEmployeeId = subscribedEmployeeIdRef.current;

			// Si ya estamos suscritos al mismo empleado y el WebSocket está activo, no hacer nada.
			if (
				previousEmployeeId === employeeId &&
				globalSocketService.isConnected()
			) {
				console.log(
					`📡 [App][SESSION:${SESSION_ID}] WebSocket ya suscrito al empleado ${employeeId}, omitiendo re-suscripción`
				);
				return;
			}

			// NOTA: Marcamos el ID de forma síncrona ANTES de los awaits para bloquear otras llamadas paralelas
			subscribedEmployeeIdRef.current = employeeId;

			if (previousEmployeeId && previousEmployeeId !== employeeId) {
				// La sesión STOMP se autentica una sola vez con el JWT del CONNECT.
				// Si cambió el empleado (p.ej. cambio de venue/empresa), hay que
				// reconectar para que beforeConnect envíe el access_token actual.
				console.log(
					`🔄 [App][SESSION:${SESSION_ID}] Empleado cambió de ${previousEmployeeId} a ${employeeId}. Forzando reconexión del WebSocket para reautenticar con el nuevo token.`
				);
				globalSocketService.disconnect();
			}

			console.log(
				`🔌 [App][SESSION:${SESSION_ID}] Inicializando WebSocket para empleado: ${employeeId}`
			);

			// Marcar como en curso para este ID específico
			initializingRef.current = employeeId;

			try {
				// Conectar WebSocket global (no-op si ya está conectado)
				await globalSocketService.connect();

				if (!isMounted) return;

				// Suscribirse al topic del employeeId del JWT (venue activa)
				const topic = `/topic/alert-notifications/${employeeId}`;
				console.log(
					`📡 [App][SESSION:${SESSION_ID}] Suscribiéndose al topic de notificaciones: ${topic}`
				);

				await globalSocketService.subscribe(topic, (notification) => {
					notificationsSocketService.onNotificationReceived(notification);
				});

				// Solo si la suscripción fue exitosa, marcamos el ID actual
				subscribedEmployeeIdRef.current = employeeId;
			} catch (error) {
				console.error(
					`❌ [App][SESSION:${SESSION_ID}] Error al inicializar WebSocket:`,
					error
				);
				// En caso de error, reseteamos para permitir reintentos
				subscribedEmployeeIdRef.current = null;
			} finally {
				if (isMounted) {
					initializingRef.current = null;
				}
			}
		}

		initializeWebSocket();

		// Cleanup: solo resetear el flag de montaje
		// No desconectamos el WebSocket aquí porque App.js nunca se desmonta durante una sesión normal
		return () => {
			isMounted = false;
		};
	}, [user?.id, loading, wsAuthEpoch]); // JWT/venue: esperar SSO y reaccionar a cambio de token

	// PostHog: identify en Haddock / Haddock HR (propiedades de persona para segmentación, p. ej. surveys por rol)
	useEffect(() => {
		const currentUser = user || authService.currentUser;
		if (!currentUser?.id) return;

		const signature = buildPosthogIdentifySignature(currentUser);
		if (posthogIdentifySignatureRef.current === signature) return;

		if (identifyHaddockUser(currentUser)) {
			posthogIdentifySignatureRef.current = signature;

			if (posthogLoginTrackedUserIdRef.current !== currentUser.id) {
				trackHaddockAdminLogin(currentUser);
				posthogLoginTrackedUserIdRef.current = currentUser.id;
			}
		}
	}, [user]);

	// Mientras estamos en proceso de SSO, mostramos un indicador de carga
	if (loading) {
		return <Loading />;
	}

	return (
		<>
			<CustomHelmet />
			<ZendeskWidget user={user || authService.currentUser} />
			<Router>
				<AppUndoBindings />
				<Switch>
					<PrivateRoute exact path="/" component={Dashboard} />
					<PrivateRoute
						exact
						path="/getting-started"
						roles={["ADMIN"]}
						component={GettingStarted}
					/>
					<Route exact path="/login" component={Login} />
					<Route
						exact
						path="/nrGHjlxFDSFGDsgvDSFA9GsaFhgpirjtyhioj2"
						component={Register}
					/>
					<Route exact path="/remember-password" component={RememberPassword} />
					<Route
						exact
						path="/remember-password/:token"
						component={NewPassword}
					/>
					<Route exact path="/invitation/:token" component={Invitation} />
					<Route
						exact
						path="/duplicated-email/:token"
						component={DuplicatedEmail}
					/>
					<Route exact path="/end-trial" component={EndTrial} />
					<Route exact path="/restricted" component={Restricted} />

					<Route exact path="/clockin" component={Clockin} />

					{domainSpecificRoutes}

					{/* LOGGED IN, ALL USERS */}
					<PrivateRoute
						exact
						path="/expenses/new"
						component={MobileExpenseFormResponsive}
					/>
					<PrivateRoute
						exact
						path="/expenses/:expenseId"
						component={MobileExpenseFormResponsive}
					/>
					<PrivateRoute exact path="/expenses" component={ExpensesResponsive} />
					<PrivateRoute path="/my-times" component={TimeRegistrations} />
					<PrivateRoute exact path="/profile" component={Profile} />
					<PrivateRoute exact path="/absences/" component={Absences} />
					<PrivateRoute exact path="/notifications" component={Notifications} />
					<SubscriptionRoute
						exact
						path="/plans/"
						feature="PLANNING"
						component={Plans}
						redirectTo="/"
					/>
					<SubscriptionRoute
						exact
						path="/plans/:group/:start@:end"
						feature="PLANNING"
						component={PlanResponsive}
						redirectTo="/"
					/>
					<SubscriptionRoute
						exact
						path="/calendar/"
						feature="MY_CALENDAR"
						component={CalendarResponsive}
						redirectTo="/"
					/>

					{/* ADMIN ONLY */}
					<PrivateRoute
						path="/times/:id/employee"
						roles={["ADMIN", "SUPERVISOR"]}
						component={TimeRegistrations}
					/>
					<PrivateRoute
						path="/settings"
						roles={["ADMIN", "SUPERVISOR"]}
						component={Settings}
					/>
					<PrivateRoute
						exact
						path="/integrations"
						roles={["ADMIN"]}
						component={Integrations}
					/>
					<Route
						exact
						path="/general-configs"
						render={() => <Redirect to="/settings" />}
					/>
					<PrivateRoute
						exact
						path="/time-registrations/import"
						roles={["ADMIN"]}
						component={TimeRegistrationsImports}
					/>
					<PrivateRoute
						exact
						path="/intelligent-plan/:group/:start@:end"
						component={PlanMagic}
					/>
					<PrivateRoute
						exact
						path="/pending-absences"
						roles={["ADMIN", "SUPERVISOR"]}
						component={AbsencesPending}
					/>
					<PrivateRoute
						exact
						path="/managed-absences"
						roles={["ADMIN", "SUPERVISOR"]}
						component={AbsencesManaged}
					/>
					<PrivateRoute
						exact
						path="/pending-expenses"
						roles={["ADMIN", "SUPERVISOR"]}
						component={PendingExpensesResponsive}
					/>
					<PrivateRoute
						exact
						path="/approved-expenses"
						roles={["ADMIN", "SUPERVISOR"]}
						component={ApprovedExpensesResponsive}
					/>
					<PrivateRoute
						exact
						path="/managed-expenses"
						roles={["ADMIN", "SUPERVISOR"]}
						component={ManagedExpenses}
					/>
					<PrivateRoute
						exact
						path="/times"
						roles={["ADMIN", "SUPERVISOR"]}
						component={Times}
					/>
					<PrivateRoute
						exact
						path="/attendance-supervisor"
						roles={["ADMIN", "SUPERVISOR"]}
						component={AttendanceSupervisorResponsive}
					/>
					<PrivateRoute
						exact
						path="/attendance-by-employee"
						roles={["ADMIN", "SUPERVISOR"]}
						component={AttendanceByEmployee}
					/>
				<PrivateRoute
					exact
					path="/without-time"
					roles={["ADMIN", "SUPERVISOR"]}
					component={TimesWithout}
				/>
				<PrivateRoute
					exact
					path="/time-balance"
					roles={["ADMIN", "SUPERVISOR"]}
					component={TimesBalance}
				/>
					<PrivateRoute
						exact
						path="/employees"
						roles={["ADMIN", "SUPERVISOR"]}
						component={Employees}
					/>
					<PrivateRoute
						exact
						path="/custom-fields"
						roles={["ADMIN"]}
						component={CustomFields}
					/>
					<PrivateRoute
						exact
						path="/employee-functions"
						roles={["ADMIN"]}
						component={EmployeeFunctions}
					/>
					<PrivateRoute
						exact
						path="/employees/import"
						roles={["ADMIN", "SUPERVISOR"]}
						component={EmployeeImports}
					/>
					<PrivateRoute
						exact
						path="/employees/shift-patterns/import"
						roles={["ADMIN", "SUPERVISOR"]}
						component={EmployeeShiftPatternsImports}
					/>
					<PrivateRoute
						exact
						path="/employees/functions/import"
						roles={["ADMIN", "SUPERVISOR"]}
						component={EmployeeFunctionsImports}
					/>
					<PrivateRoute
						exact
						path="/employees/ubications/import"
						roles={["ADMIN", "SUPERVISOR"]}
						component={EmployeeUbicationsImports}
					/>
					<PrivateRoute
						exact
						path="/functions/import"
						roles={["ADMIN"]}
						component={FunctionsImports}
					/>
					<PrivateRoute
						exact
						path="/ubications/import"
						roles={["ADMIN"]}
						component={UbicationsImports}
					/>
					<PrivateRoute
						path="/employees/:id"
						roles={["ADMIN", "SUPERVISOR"]}
						component={Employee}
					/>
					<PrivateRoute
						exact
						path="/absences/import"
						roles={["ADMIN", "SUPERVISOR"]}
						component={AbsenceImports}
					/>
					<PrivateRoute
						exact
						path="/groups/"
						roles={["ADMIN", "SUPERVISOR"]}
						component={Groups}
					/>
					<PrivateRoute
						exact
						path="/groups/:id"
						roles={["ADMIN", "SUPERVISOR"]}
						component={Group}
					/>
					<PrivateRoute
						exact
						path="/staffing-levels/"
						roles={["ADMIN", "SUPERVISOR"]}
						component={StaffingLevels}
					/>
					<PrivateRoute
						exact
						path="/shift-patterns/import"
						roles={["ADMIN"]}
						component={ShiftPatternsImports}
					/>
					<PrivateRoute
						exact
						path="/staffing-levels/import"
						roles={["ADMIN"]}
						component={StaffingLevelsImports}
					/>
					<PrivateRoute
						exact
						path="/shifts/import"
						roles={["ADMIN"]}
						component={ShiftsImports}
					/>

					<PrivateRoute
						exact
						path="/shifts/"
						roles={["ADMIN", "SUPERVISOR"]}
						component={Shifts}
					/>
					<PrivateRoute
						exact
						path="/shift-patterns/"
						roles={["ADMIN", "SUPERVISOR"]}
						component={ShiftPatterns}
					/>
					<PrivateRoute exact path="/shift-exchanges/" component={ShiftExchanges} />
					<PrivateRoute
						exact
						path="/pending-shift-exchanges"
						roles={["ADMIN", "SUPERVISOR"]}
						component={ShiftExchangesPending}
					/>
					<PrivateRoute
						exact
						path="/managed-shift-exchanges"
						roles={["ADMIN", "SUPERVISOR"]}
						component={ShiftExchangesManaged}
					/>
				<PrivateRoute exact path="/my-preferences/" component={Preferences} />
					<PrivateRoute
						exact
						path="/pending-preferences/"
						roles={["ADMIN", "SUPERVISOR"]}
						component={PendingPreferences}
					/>
					<PrivateRoute
						exact
						path="/managed-preferences/"
						roles={["ADMIN", "SUPERVISOR"]}
						component={ManagedPreferences}
					/>
					<PrivateRoute
						exact
						path="/time-types/"
						roles={["ADMIN"]}
						component={TimeTypes}
					/>
					<PrivateRoute
						exact
						path="/rules/"
						roles={["ADMIN"]}
						component={Rules}
					/>
					<PrivateRoute
						exact
						path="/transactions/:employee/:account/:start@:end"
						roles={["ADMIN"]}
						component={Transactions}
					/>
					<PrivateRoute
						exact
						path="/transactions/import"
						roles={["ADMIN"]}
						component={TransactionsImports}
					/>
					<PrivateRoute
						exact
						path="/transactions/"
						roles={["ADMIN"]}
						component={Transactions}
					/>
					<PrivateRoute
						exact
						path="/calendars/"
						roles={["ADMIN"]}
						component={Calendars}
					/>
					<PrivateRoute
						exact
						path="/calendars/import"
						roles={["ADMIN"]}
						component={CalendarImports}
					/>
					<PrivateRoute
						exact
						path="/calendars-system/"
						roles={["ADMIN"]}
						component={SystemCalendars}
					/>
					<PrivateRoute
						exact
						path="/calendars-system/import"
						roles={["ADMIN"]}
						component={SystemCalendarImports}
					/>
					<PrivateRoute
						exact
						path="/day-types/"
						roles={["ADMIN"]}
						component={DayTypes}
					/>
					<PrivateRoute
						exact
						path="/accounts/"
						roles={["ADMIN"]}
						component={Accounts}
					/>
					<PrivateRoute
						exact
						path="/login-attempts"
						roles={["ADMIN"]}
						component={LoginAttempts}
					/>
					<PrivateRoute
						exact
						path="/contracts/import"
						roles={["ADMIN"]}
						component={ContractsImports}
					/>
					<PrivateRoute
						exact
						path="/contracts/"
						roles={["ADMIN"]}
						component={Contracts}
					/>

					<PrivateRoute
						exact
						path="/subscriptions"
						roles={["ADMIN"]}
						component={Subscriptions}
					/>

					<PrivateRoute
						exact
						path="/my-company"
						roles={["ADMIN"]}
						component={MyCompany}
					/>

					<PrivateRoute
						exact
						path="/plan-views/"
						roles={["ADMIN", "SUPERVISOR"]}
						component={PlanViews}
					/>

					<PrivateRoute
						exact
						path="/plans/import"
						roles={["ADMIN"]}
						component={PlanImports}
					/>

					<SubscriptionRoute
						exact
						path="/forecasting"
						feature="FORECASTING"
						roles={["ADMIN"]}
						component={Forecasting}
						redirectTo="/plans"
					/>

					<PrivateRoute exact path="/documents" component={Documents} />
					<PrivateRoute
						exact
						path="/employee-ubications"
						component={EmployeeUbications}
					/>
					<PrivateRoute
						exact
						path="/documents/:category/"
						component={Documents}
					/>

					<PrivateRoute
						exact
						path="/store-folders/"
						roles={["ADMIN", "SUPERVISOR"]}
						component={StoreFolders}
					/>

					<PrivateRoute
						exact
						path="/widget-templates/"
						roles={["ADMIN", "SUPERVISOR"]}
						component={WidgetTemplates}
					/>

					<PrivateRoute
						exact
						path="/providers"
						roles={["ADMIN", "SUPERVISOR"]}
						component={Providers}
					/>
					<PrivateRoute
						exact
						path="/expense-groupings"
						component={ExpenseGroupings}
					/>
					<PrivateRoute
						exact
						path="/expense-types"
						roles={["ADMIN", "SUPERVISOR"]}
						component={ExpenseTypes}
					/>
					<PrivateRoute
						exact
						path="/payment-types"
						roles={["ADMIN", "SUPERVISOR"]}
						component={PaymentTypes}
					/>

					{/* NOT FOUND */}
					<PrivateRoute component={NotFound} />
				</Switch>
			</Router>
		</>
	);
}

export default App;

{
	/* <PrivateRoute
exact
path="/documents/"
roles={"ADMIN"}
component={Documents}
/>  */
}
